import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/Button";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";
import { useAuth } from "../auth/useAuth";
import { ExpenseDialog } from "./ExpenseDialog";
import { announceMoneyChanged } from "../quotes/MoneyStrip";
import type { Expense, Profit } from "../../api/types";
import { formatMoney } from "../../lib/money";

// 報價單上的「這張單的成本」(第 16 輪 Task 3)。
//
// 業務要看得到自己那張單賺不賺錢 —— 那不是只有財務該關心的事,
// 而且**看得到成本的人才會去報帳**。
//
// --------------------------------------------------------------------------
// 沒登錄任何雜支的專案一定要標出來
// --------------------------------------------------------------------------
// 那種專案的毛利率會是 100%,而那幾乎一定是「還沒報帳」而不是「真的沒有成本」。
// 不標的話,第 17 輪的績效表會把一個還沒報帳的案子排在最賺錢的第一名 ——
// 看起來完全正常,而結論是錯的。
//
// --------------------------------------------------------------------------
// 為什麼不用 react-query
// --------------------------------------------------------------------------
// 這一塊掛在報價單編輯器裡,而編輯器那棵樹的測試沒有 `QueryClientProvider`
// (`AttachmentPanel` 也是為了同一個理由用 `useEffect` + `api`)。
// 跟著既有的慣例,好過逼十一個測試檔都去包一層它們本來不需要的東西。

/** 金額一律走 decimal.js。`Number` 算出來的角分會直接印給人看。 */
function money(value: string | number | null | undefined): string {
  // 全系統金額一律 0,000,000、四捨五入到整數(業主 2026-10-15)
  return formatMoney(value);
}

export interface ProjectCostProps {
  quoteId: number;
  /** 這個人對這張單唯讀嗎(財務)。 */
  readOnly?: boolean;
}

export function ProjectCost({ quoteId, readOnly = false }: ProjectCostProps) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const toast = useToast();
  const { user } = useAuth();

  const [rows, setRows] = useState<Expense[]>([]);
  const [p, setP] = useState<Profit | null>(null);

  const refresh = useCallback(() => {
    // 兩趟各自吞掉例外**而且檢查形狀**:成本讀不到不該讓整個編輯器壞掉 ——
    // 使用者該看到的仍然是報價單本體,不是一片白畫面。
    //
    // 形狀也要擋:`api.get` 回什麼就是什麼,拿到不是陣列的東西直接 `.map()`
    // 會在 render 當場丟例外,而 React 會把**整棵樹**卸載 ——
    // 報價單本體跟著消失,而真正壞掉的只是底下這一小塊。
    api
      .get<Expense[]>(`/quotes/${quoteId}/expenses`)
      .then((got) => setRows(Array.isArray(got) ? got : []))
      .catch(() => undefined);
    api
      .get<Profit>(`/quotes/${quoteId}/profit`)
      .then((got) => setP(got && typeof got === "object" && "revenue" in got ? got : null))
      .catch(() => undefined);
  }, [quoteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const remove = async (row: Expense) => {
    try {
      await api.del(`/expenses/${row.id}`);
      toast.push("已刪除", "success");
      refresh();
      announceMoneyChanged();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "刪不掉", "error");
    }
  };

  /** 改得動嗎?**登錄者本人、財務、老闆** —— 與後端的 `_may_edit()` 同一條規則。 */
  const mayEdit = (row: Expense): boolean =>
    user !== null &&
    (row.created_by === user.id || user.role === "OWNER" || user.role === "FINANCE");

  return (
    <section className="flex flex-col gap-3" aria-label="這張單的成本">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-base font-semibold text-[var(--text)]">這張單的成本</h3>
        <Button
          size="sm"
          icon={<Plus aria-hidden="true" size={14} />}
          disabled={readOnly}
          title={readOnly ? "財務帳號對業務資料唯讀,雜支請到「雜支」那一頁登錄" : undefined}
          onClick={() => setAdding(true)}
        >
          登錄雜支
        </Button>
      </div>

      {p ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-[var(--text-3)]">營收(成交金額)</dt>
            <dd className="m-0 tabular-nums">{money(p.revenue)}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--text-3)]">成本(雜支)</dt>
            <dd className="m-0 tabular-nums">{money(p.cost)}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--text-3)]">毛利</dt>
            <dd className="m-0 tabular-nums font-semibold">{money(p.profit)}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--text-3)]">毛利率</dt>
            <dd className="m-0 tabular-nums">
              {/* **沒有收入時說「還沒有收入」,不是「毛利 0%」** —— 那兩件事不一樣,
                  後者會讓人以為這個案子做白工了。 */}
              {p.margin_percent === null ? (
                <span className="text-[var(--text-3)]">還沒有收入</span>
              ) : (
                `${p.margin_percent} %`
              )}
            </dd>
          </div>
        </dl>
      ) : null}

      {p && !p.cost_recorded ? (
        // 見檔頭:不標的話,績效表會把一個還沒報帳的案子排在最賺錢的第一名。
        <p
          role="status"
          className="m-0 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--text-2)]"
        >
          <strong>尚未登錄任何成本</strong> —— 上面的毛利只是還沒報帳,不是真的沒有花費。
        </p>
      ) : null}

      {rows.length === 0 ? (
        // **不用 EmptyState** —— 它帶著兔子吉祥物,而第五輪定下的「安靜編輯」是
        // 報價單編輯器整頁都不放品牌素材(`tests/brand.test.tsx` 守著)。
        // 報價單是看數字的工具,裝飾不該滲進來。
        <p className="m-0 text-sm text-[var(--text-3)]">
          這張單還沒有登錄雜支。車資、材料、便當都算 —— 報了帳,毛利才算得準。
        </p>
      ) : (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table aria-label="這張單的雜支" className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                <th scope="col" className="px-3 py-2">日期</th>
                <th scope="col" className="px-3 py-2">分類</th>
                <th scope="col" className="px-3 py-2">店家</th>
                <th scope="col" className="px-3 py-2">金額</th>
                <th scope="col" className="px-3 py-2">登錄者</th>
                <th scope="col" className="px-3 py-2">動作</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2">{row.spent_on}</td>
                  <td className="px-3 py-2">{row.category_label}</td>
                  <td className="px-3 py-2">{row.vendor}</td>
                  <td className="px-3 py-2 tabular-nums">{money(row.amount)}</td>
                  <td className="px-3 py-2">{row.recorder_name}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={!mayEdit(row)}
                        title={mayEdit(row) ? undefined : "這筆是別人登錄的,只有他本人、財務或老闆改得動"}
                        onClick={() => setEditing(row)}
                      >
                        修改
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        icon={<Trash2 aria-hidden="true" size={14} />}
                        disabled={!mayEdit(row)}
                        title={mayEdit(row) ? undefined : "這筆是別人登錄的,只有他本人、財務或老闆刪得掉"}
                        onClick={() => void remove(row)}
                      >
                        刪除
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ExpenseDialog
        key={`new-${adding}`}
        open={adding}
        quoteId={quoteId}
        onClose={() => setAdding(false)}
        onDone={() => {
          setAdding(false);
          refresh();
          announceMoneyChanged();
        }}
      />
      <ExpenseDialog
        key={`edit-${editing?.id ?? 0}`}
        open={editing !== null}
        expense={editing ?? undefined}
        quoteId={quoteId}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null);
          refresh();
          announceMoneyChanged();
        }}
      />
    </section>
  );
}
